import cn from 'classnames'
import React from 'react'
import ReactDOM from 'react-dom'
import styled from '@emotion/styled'

import SearchSuggestions from 'components/SearchSuggestions'
import ListenToKeyboard from 'components/ListenToKeyboard'
import { ENTER, ESCAPE, LEFT, RIGHT } from 'constants/keys'
import { SVGIcon } from 'shared/ui'

import { getValueByPath, isEmpty } from 'util/objects'
import getSearchQueryPartAtPosition from 'util/search/getSearchQueryPartAtPosition'

import styles from './styles.css'

const shouldUpdateQueryStringSelection = (selStart, selEnd, value, options) => {
  /**
   * (pnagy)
   * The next if is a hack that we need, thanks to the wonderful Safari.
   * Safari fires a focus event when we set the selection range of the
   * field. The only way to prevent this is to make sure that the selection
   * we store is exactly the same as the selection in the field. At this
   * point here we send the real selection of the search box which contains
   * an extra space when we selected the values by clicking in the list.
   * Later when we submit the search we'll trim this space and the updated
   * text in the box won't contain it. BUT we have a stored value and that
   * value we'll have one more character. This code makes sure that we have
   * a good selection range stored. Notice we only need this check when
   * submitting the search.
   */
  return selEnd === selStart && value.match(/(.*)[\s]$/) && options.submit
}

class Search extends React.Component {
  state = {}

  componentDidMount() {
    const { doFetchTopLabels, topLabels } = this.props
    if (!topLabels) doFetchTopLabels(null)
  }

  componentDidUpdate() {
    const { searchBoxRef } = this
    if (searchBoxRef) {
      const { searchSelectionStart } = this.props
      const { selectionStart } = searchBoxRef
      if (searchSelectionStart && searchSelectionStart !== selectionStart) {
        searchBoxRef.setSelectionRange(
          searchSelectionStart,
          searchSelectionStart
        )
      }
    }
  }

  doFetchLabels = searchQuery => {
    const { searchBoxRef } = this
    if (searchBoxRef) {
      const { selectionStart } = searchBoxRef
      const { doSearchLabels, labelsByTerm } = this.props
      const currentPart = getSearchQueryPartAtPosition(
        searchQuery,
        selectionStart
      )
      if (!currentPart) return
      const { operator, value } = currentPart

      if (
        (operator === 'tag' || operator === 'keywords') &&
        !labelsByTerm[value]
      ) {
        doSearchLabels(value, { storeTerm: false })
      }
    }
  }

  doUpdateTicketSearchQuery(queryString, options = {}) {
    const { searchBoxRef } = this
    if (searchBoxRef) {
      const { selectionEnd, selectionStart, value } = searchBoxRef
      const selection = { selectionStart, selectionEnd }
      if (
        shouldUpdateQueryStringSelection(
          selectionStart,
          selectionEnd,
          value,
          options
        )
      ) {
        selection.selectionStart = value.trim().length
        selection.selectionEnd = value.trim().length
      }
      this.props.doUpdateTicketSearchQuery(queryString, {
        ...options,
        ...selection,
      })
    }
  }

  handleInput = ev => {
    const { value } = ev.target
    this.doUpdateTicketSearchQuery(value, {
      commit: true,
      submit: false,
    })
    this.doFetchLabels(value)
  }

  handleKeyDown = ev => {
    if (ev.keyCode === ESCAPE) {
      this.handleClearClick()
      this.handleBackClick()
      this.searchBoxRef.blur()
    }
  }

  handleKeyUp = e => {
    const { searchQuery } = this.props
    if (e.keyCode === LEFT || e.keyCode === RIGHT) {
      this.doUpdateTicketSearchQuery(searchQuery, {
        commit: true,
        submit: false,
      })
      this.doFetchLabels(this.searchBoxRef.value)
    }
    if (e.keyCode === ENTER && this.isNoSuggestions()) this.handleSubmit()
  }

  handleClearClick = () => {
    const { onClearClick, conversationListBeforeSearch } = this.props
    // eslint-disable-next-line react/no-find-dom-node
    ReactDOM.findDOMNode(this.searchBoxRef).value = ''
    this.setState({
      searchBoxOpen: false,
    })
    this.doUpdateTicketSearchQuery('', {
      commit: true,
      submit: false,
    })
    if (conversationListBeforeSearch) onClearClick()
    this.searchBoxRef.focus()
  }

  handleSearchBoxFocused = () => {
    this.setState({
      isSearchBoxFocused: true,
    })
    this.props.doToggleListSearchBoxFocused(true)
  }
  handleSearchBoxClick = () => {
    this.searchBoxRef.focus()
    const { searchQuery, isTypedSearch } = this.props
    this.doUpdateTicketSearchQuery(isTypedSearch ? searchQuery : '', {
      commit: true,
      submit: false,
    })
    this.doFetchLabels(this.searchBoxRef.value)
  }

  handleSearchBoxBlur = () => {
    // moveCursorToEnd will blur and then immediately refocus to keep the cursor
    // in view. So we'll just check that a focus isn't about to happen.
    if (!this.willRefocus) {
      this.setState({ searchBoxOpen: false, isSearchBoxFocused: false })
      this.props.doToggleListSearchBoxFocused(false)
    }
  }

  handleCurtainClick = ev => {
    const { doToggleListSearchBoxFocused, onSearchCloseClick } = this.props
    this.setState({ isSearchBoxFocused: false })
    doToggleListSearchBoxFocused(false)
    if (onSearchCloseClick) onSearchCloseClick(ev)
  }

  handleBackClick = ev => {
    const { onBackToListClick } = this.props
    this.setState({
      searchBoxOpen: false,
      isSearchBoxFocused: false,
    })
    if (onBackToListClick) onBackToListClick(ev)
  }

  handleSelect = queryString => {
    this.doUpdateTicketSearchQuery(queryString, {
      amend: true,
      commit: true,
      submit: false,
    })
  }

  handleChange = (queryString, event) => {
    if (!event || event.type !== 'keydown' || event.keyCode === ENTER) return
    this.doUpdateTicketSearchQuery(queryString, {
      amend: true,
      commit: false,
      submit: false,
    })
  }

  handleChangeToSubmit = () => {
    const {
      committedSearchQuery,
      uncommittedSearchQuery,
      searchSelectionStart,
    } = this.props
    if (!uncommittedSearchQuery || !committedSearchQuery) return false

    const position =
      searchSelectionStart -
      (uncommittedSearchQuery.length - committedSearchQuery.length)
    return this.doUpdateTicketSearchQuery(committedSearchQuery, {
      commit: false,
      submit: false,
      position,
    })
  }

  handleSubmit = () => {
    const { committedSearchQuery, isFreeFormSearch } = this.props
    this.setState({ isSearchBoxFocused: false })
    this.doUpdateTicketSearchQuery(committedSearchQuery, {
      commit: true,
      reset: !isFreeFormSearch,
      submit: true,
    })
    this.props.doToggleListSearchBoxFocused(false)
    if (this.searchBoxRef) {
      this.searchBoxRef.blur()
    }
  }

  takeSearchBoxRef = el => {
    this.searchBoxRef = el
  }

  handleMouseDown = e => {
    e.preventDefault()
  }

  moveCursorToEnd = () => {
    this.willRefocus = true
    this.searchBoxRef.blur()
    this.searchBoxRef.focus()
    this.willRefocus = false
  }

  isNoSuggestions = () => {
    const {
      suggestions,
      previousLabelSuggestionSize: prevLabelSize,
    } = this.props
    const allSuggestionsCount = Object.values(this.props.suggestions).reduce(
      (output, value) => {
        return output + value.length
      },
      0
    )
    return (
      allSuggestionsCount === 0 ||
      (prevLabelSize === 0 &&
        suggestions.label &&
        suggestions.label[0] &&
        suggestions.label[0].isLoading &&
        allSuggestionsCount === 1)
    )
  }

  // We want to stop the curtain click event
  // when someone clicks around the search input
  // so that we don't close search because of an accidental
  // click
  stopCurtainClick = ev => {
    ev.stopPropagation()
  }

  // If we have a folder search e.g. `folder:1464931230` we show the name of the
  // folder instead of the id.
  searchQueryLabel = () => {
    const { foldersById: folders, searchQuery, isTypedSearch } = this.props

    if (!isTypedSearch) return ''

    if (!folders || isEmpty(folders)) return searchQuery

    const regex = /folder:(\d+)/
    const matches = searchQuery.match(regex)
    const folderId = matches && matches[1]

    if (!folderId) return searchQuery

    const name = getValueByPath(`${folderId}.name`, folders)

    if (!name) return searchQuery

    const replaced = searchQuery.replace(regex, `folder:"${name}"`)
    return replaced
  }

  render() {
    const {
      className,
      committedSearchQuery,
      previousLabelSuggestionSize: prevLabelSize,
      classicView,
    } = this.props
    const { isSearchBoxFocused, clearActiveIndex } = this.state
    return (
      <div className={className}>
        <ListenToKeyboard
          onForwardSlash={this.handleSearchBoxClick}
          disableForInput
          preventDefault
        />
        <div className={styles.searchAndSuggestionsBox}>
          <div
            onMouseDown={this.stopCurtainClick}
            className={styles.aboveCurtain}
          >
            <div className={styles.searchBox}>
              <input
                ref={this.takeSearchBoxRef}
                onClick={this.handleSearchBoxClick}
                onKeyDown={this.handleKeyDown}
                onKeyUp={this.handleKeyUp}
                onFocus={this.handleSearchBoxFocused}
                onBlur={this.handleSearchBoxBlur}
                className={cn(styles.searchBox_input, {
                  [styles.searchBox_input_classic_view]: classicView,
                })}
                onChange={this.handleInput}
                name="term"
                placeholder={
                  classicView ? `Search ${app.t('tickets')}` : 'Search...'
                }
                autoComplete="off"
                value={this.searchQueryLabel()}
              />
              <SVGIcon
                name="times"
                color="color.monochrome.mediumDark"
                size={16}
                className={cn(
                  styles.icon,
                  styles.searchBox_clearIcon,
                  styles['icon--opacity--dimmed']
                )}
                onClick={this.handleClearClick}
              />
              <SVGIcon
                name="search"
                color="color.monochrome.mediumDark"
                size={16}
                className={cn(
                  styles.icon,
                  styles.searchBox_backIcon,
                  { [styles.searchBox_backIcon_classic_view]: classicView },
                  styles['icon--opacity--dimmed']
                )}
              />
              {isSearchBoxFocused && (
                <SearchSuggestions
                  searchQuery={committedSearchQuery}
                  focusElement={this.searchBoxRef}
                  clearActiveIndex={clearActiveIndex}
                  onSubmit={this.handleSubmit}
                  onSelect={this.handleSelect}
                  onChange={this.handleChange}
                  onChangeToSubmit={this.handleChangeToSubmit}
                  onMouseDown={this.handleMouseDown}
                  previousLabelSuggestionSize={prevLabelSize}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default styled(Search)`
  position: relative;
`
